// Web Push subscribe/unsubscribe helpers for the browser.
//
// Flow: the service worker (public/sw.js, registered in main.tsx) owns the
// PushManager. Here we request notification permission, subscribe with our
// VAPID public key, and persist the resulting subscription server-side via the
// save_push_subscription RPC so the push-send Edge Function can reach it.
//
// The iOS app has no PushManager (WKWebView), so on iOS every export here
// answers for APNs instead (lib/native: registerPush → save_device_token), and
// PushNudge and the Settings toggle work unchanged in both places.

import { PushNotifications } from '@capacitor/push-notifications'
import { supabase } from './supabase'
import { VAPID_PUBLIC_KEY } from './config'
import { nativePushPlatform, nativePushPermission, registerPush } from './native'

const APNS_TOKEN_KEY = 'va.apnsToken'
// iOS answers permission asynchronously; the sync reader below returns the
// last answer seen, and pushPermissionNow() refreshes it.
let nativePerm: PushState = 'default'

export type PushState = 'unsupported' | 'default' | 'denied' | 'granted'

export function pushSupported(): boolean {
  if (nativePushPlatform()) return true
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  )
}

export function pushPermission(): PushState {
  if (nativePushPlatform()) return nativePerm
  if (!pushSupported()) return 'unsupported'
  return Notification.permission as PushState
}

/** pushPermission, asked fresh — the only accurate answer on iOS. */
export async function pushPermissionNow(): Promise<PushState> {
  if (!nativePushPlatform()) return pushPermission()
  const p = await nativePushPermission()
  nativePerm = p === 'granted' ? 'granted' : p === 'denied' ? 'denied' : p === 'prompt' ? 'default' : 'unsupported'
  return nativePerm
}

// VAPID public keys are base64url; PushManager wants a Uint8Array.
function urlB64ToUint8Array(base64: string): Uint8Array {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4)
  const b64 = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/')
  const raw = atob(b64)
  const out = new Uint8Array(raw.length)
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i)
  return out
}

// The push service returns keys as ArrayBuffers; encode them base64url for the DB.
function bufToB64url(buf: ArrayBuffer | null): string {
  if (!buf) return ''
  const bytes = new Uint8Array(buf)
  let s = ''
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i])
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

// A subscription is bound to the VAPID key it was created with: rotate the pair
// and the old one answers 403 forever, while getSubscription() keeps returning
// it as if nothing were wrong. So check which key it carries before reusing it.
function subscribedWithCurrentKey(sub: PushSubscription): boolean {
  const key = sub.options.applicationServerKey
  // Older browsers don't expose it — assume it's ours rather than churning a
  // subscription that probably works.
  if (!key) return true
  return bufToB64url(key) === VAPID_PUBLIC_KEY
}

async function readyRegistration(): Promise<ServiceWorkerRegistration | null> {
  if (!pushSupported()) return null
  try {
    // main.tsx registers /sw.js on load; ready resolves once it's active.
    return await navigator.serviceWorker.ready
  } catch {
    return null
  }
}

export async function isPushSubscribed(): Promise<boolean> {
  if (nativePushPlatform()) {
    if ((await pushPermissionNow()) !== 'granted') return false
    try { return !!localStorage.getItem(APNS_TOKEN_KEY) } catch { return false }
  }
  const reg = await readyRegistration()
  if (!reg) return false
  const sub = await reg.pushManager.getSubscription()
  return !!sub
}

// Ask permission (if needed), subscribe, and persist. Returns true on success.
// Throws only for unexpected errors; a denied permission resolves to false.
export async function enablePush(): Promise<boolean> {
  if (nativePushPlatform()) {
    const token = await registerPush()
    await pushPermissionNow()
    return !!token
  }
  if (!pushSupported() || !supabase) return false

  const permission = await Notification.requestPermission()
  if (permission !== 'granted') return false

  const reg = await readyRegistration()
  if (!reg) return false

  let sub = await reg.pushManager.getSubscription()
  if (sub && !subscribedWithCurrentKey(sub)) {
    // Left over from a previous VAPID pair. Drop it here as well as server-side,
    // otherwise push-send keeps a row it can never deliver to (it only prunes
    // 404/410, and a key mismatch is a 403).
    const stale = sub.endpoint
    try {
      await sub.unsubscribe()
    } catch {
      /* ignore */
    }
    await supabase.rpc('delete_push_subscription', { p_endpoint: stale })
    sub = null
  }
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlB64ToUint8Array(VAPID_PUBLIC_KEY) as BufferSource,
    })
  }

  const json = sub.toJSON()
  const keys = json.keys || {}
  const p256dh = keys.p256dh || bufToB64url(sub.getKey('p256dh'))
  const auth = keys.auth || bufToB64url(sub.getKey('auth'))

  const { error } = await supabase.rpc('save_push_subscription', {
    p_endpoint: sub.endpoint,
    p_p256dh: p256dh,
    p_auth: auth,
    p_user_agent: navigator.userAgent,
  })
  if (error) throw error
  return true
}

// Unsubscribe locally and remove the row server-side.
export async function disablePush(): Promise<void> {
  if (nativePushPlatform()) {
    // Unregistering retires the token at Apple, so push-send's next attempt
    // gets 410 and deletes the row itself. The iOS permission stays as it is.
    try { await PushNotifications.unregister() } catch { /* ignore */ }
    try { localStorage.removeItem(APNS_TOKEN_KEY) } catch { /* ignore */ }
    return
  }
  const reg = await readyRegistration()
  if (!reg) return
  const sub = await reg.pushManager.getSubscription()
  if (!sub) return
  const endpoint = sub.endpoint
  try {
    await sub.unsubscribe()
  } catch {
    /* ignore */
  }
  if (supabase) {
    await supabase.rpc('delete_push_subscription', { p_endpoint: endpoint })
  }
}
